import React, { useState, useEffect, useContext } from 'react';
import {
  ApartmentOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MessageOutlined,
  MoonOutlined,
  PartitionOutlined,
  ScheduleOutlined,
  ShareAltOutlined,
  SunOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate, useLocation } from 'react-router';
import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { PagePartOutlet } from '@owl-frontend/components';
import { SessionContext } from '../../context/AppContainer/AppContainer';
import { actions as sessionSliceActions } from '../../data/session/logic';
import type { StoreState } from '../../data/store';
import { hasFalconRole } from '../../helpers/roles';
import { useTaskCount } from '../../pages/workers/hooks/useTaskCount';
import { RouteDefs } from '../Routing/routes';
import styles from './MainContainer.module.scss';

/** Keep in sync with $rail-width-* in src/styles/variables.scss */
const RAIL_COLLAPSED = 48;
const RAIL_EXPANDED = 200;

const COLLAPSED_STORAGE_KEY = 'falcon.nav.collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {} // eslint-disable-line no-empty
}

/**
 * The rail collapses to icons, so every item needs one to stay identifiable at
 * 48px. `data-testid` goes on the item rather than the label because antd hides
 * labels when collapsed, and a hidden label is not clickable from a test.
 */
function navItem(
  key: string,
  label: React.ReactNode,
  icon: React.ReactNode,
  testId: string
): NonNullable<MenuProps['items']>[number] {
  return {
    key,
    icon,
    label,
    title: typeof label === 'string' ? label : undefined,
    'data-testid': testId,
  };
}

const MainContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const messages = useSelector((s: StoreState) => s['localization'].messages);
  const session = useContext(SessionContext);
  const currentUser = session.current;
  const roles = currentUser?.roles;
  const darkMode = useSelector((s: StoreState) => s['session'].darkMode);
  const [totalTimeOnPlatform, setTotalTimeOnPlatform] = useState<string | null>(
    null
  );
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const taskCount = useTaskCount();
  const currentPath = location.pathname;
  const currentSearch = location.search;
  const isOnTasksPage = currentPath === RouteDefs.Tasks && currentSearch === '';

  useEffect(() => {
    if (isOnTasksPage) {
      setTotalTimeOnPlatform(taskCount.totalTimeOnPlatform);
    } else {
      setTotalTimeOnPlatform(null);
    }
  }, [isOnTasksPage, taskCount.totalTimeOnPlatform]);

  const onCollapse = React.useCallback((next: boolean) => {
    setCollapsed(next);
    writeCollapsed(next);
  }, []);

  const onMenuClick = React.useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      if (key === 'logout') {
        dispatch(sessionSliceActions.logout());
        return;
      }

      if (key === 'dark-mode') {
        dispatch(sessionSliceActions.setDarkMode(!darkMode));
        return;
      }

      navigate(key);
    },
    [navigate, dispatch, darkMode]
  );

  const menuItems = React.useMemo(() => {
    const items: NonNullable<MenuProps['items']> = [];
    const isAdmin = roles && roles.indexOf('admin') > -1;
    const isTenantUserManagement =
      roles && roles.indexOf('tenant-user-management') > -1;
    if (isAdmin) {
      items.push(
        navItem(
          RouteDefs.Tenants,
          messages['tenants.title'],
          <ApartmentOutlined />,
          'tenantsPage-btn'
        )
      );
      items.push(
        navItem(
          RouteDefs.Users,
          messages['users.title'],
          <TeamOutlined />,
          'usersPage-btn'
        )
      );
      items.push(
        navItem(
          RouteDefs.FowlTopologies,
          messages['fowlTopologies.title'],
          <PartitionOutlined />,
          'topologiesPage-btn'
        )
      );
      items.push(
        navItem(
          RouteDefs.FulfillmentCapacity,
          messages['fulfillmentCapacity.title'],
          <ScheduleOutlined />,
          'fulfillmentPage-btn'
        )
      );
    }

    if (isTenantUserManagement) {
      items.push(
        navItem(
          RouteDefs.TenantUsers,
          messages['tenants.usersTitle'],
          <IdcardOutlined />,
          'tenantUsers-btn'
        )
      );
    }

    items.push(
      navItem(
        RouteDefs.Tasks,
        messages['workers.tasks.title'],
        <UnorderedListOutlined />,
        'tasksPage-btn'
      )
    );

    items.push(
      navItem(
        RouteDefs.Feedback,
        messages['feedback.title'],
        <MessageOutlined />,
        'feedbackPage-btn'
      )
    );

    if (hasFalconRole(currentUser, 'file-share')) {
      items.push(
        navItem(
          RouteDefs.FileShare,
          messages['fileShare.title'],
          <ShareAltOutlined />,
          'fileSharePage-btn'
        )
      );
    }

    if (hasFalconRole(currentUser, 'file-share-admin')) {
      items.push(
        navItem(
          RouteDefs.PigeonUsers,
          messages['pigeonUsers.title'],
          <UserAddOutlined />,
          'pigeonUsersPage-btn'
        )
      );
    }

    return items;
  }, [roles, messages, currentUser]);

  const footerItems = React.useMemo(
    () => [
      navItem(
        'dark-mode',
        darkMode ? messages['main.lightMode'] : messages['main.darkMode'],
        darkMode ? <SunOutlined /> : <MoonOutlined />,
        'dark-mode-btn'
      ),
      navItem(
        'logout',
        messages['main.logout'],
        <LogoutOutlined />,
        'logout-btn'
      ),
    ],
    [messages, darkMode]
  );

  const selectedKeys = React.useMemo(() => {
    const routes = menuItems
      .map((item) => item && 'key' in item && item.key)
      .filter((key): key is string => typeof key === 'string');

    // Nested routes (/tenants/:tenantId) keep their section highlighted.
    const match = routes
      .filter((route) => currentPath.startsWith(route))
      .sort((a, b) => b.length - a.length)[0];

    return match ? [match] : [];
  }, [menuItems, currentPath]);

  return (
    <Layout className={styles.shell} hasSider>
      <Layout.Sider
        className={styles.sider}
        theme="light"
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        collapsedWidth={RAIL_COLLAPSED}
        width={RAIL_EXPANDED}
      >
        <nav className={styles.nav} data-testid="main-menu" aria-label="menu">
          <Menu
            className={styles.navPrimary}
            mode="inline"
            inlineIndent={12}
            selectedKeys={selectedKeys}
            items={menuItems}
            onClick={onMenuClick}
          />
          <Menu
            className={styles.navFooter}
            mode="inline"
            inlineIndent={12}
            selectable={false}
            items={footerItems}
            onClick={onMenuClick}
          />
        </nav>
      </Layout.Sider>

      <Layout className={styles.body}>
        <Layout.Header className={styles.header}>
          <div className={styles.headerLeft}>
            <PagePartOutlet part="breadcrumbs" />
            <PagePartOutlet part="topPanelExtra" />
          </div>
          {totalTimeOnPlatform && isOnTasksPage && (
            <div className={styles.timeSpent}>
              {intl.formatMessage(
                { id: 'workers.tasks.timeOnPlatform.title' },
                { timeOnPlatform: totalTimeOnPlatform }
              )}
            </div>
          )}
          <div className={styles.headerRight}>
            <PagePartOutlet part="topPanelActions" />
          </div>
        </Layout.Header>

        <Layout.Content className={styles.content}>
          <Outlet />
        </Layout.Content>
      </Layout>
    </Layout>
  );
};

export default MainContainer;
